import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon, type IconName } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import type { MessageKey } from '@/shared/i18n/messages';

export interface NodeAction {
  readonly id: string;
  readonly labelKey: MessageKey;
  readonly icon: IconName;
  readonly danger?: boolean;
  readonly run: () => void;
}

interface Props {
  /** For the trigger's accessible name — a row of identical "More" buttons is
   *  unusable with a screen reader. */
  nodeName: string;
  actions: readonly NodeAction[];
}

/**
 * The `…` menu on a card.
 *
 * On a phone it is a bottom sheet rather than a dropdown: a 160px popover
 * anchored to a card in a two-column grid ends up half off-screen, and the
 * actions are the same either way.
 */
export function NodeMenu({ nodeName, actions }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [desktopPosition, setDesktopPosition] = useState({ top: 8, left: 8, maxHeight: 0 });
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const positionDesktopMenu = useCallback(() => {
    const triggerElement = trigger.current;
    const menuElement = menu.current;
    if (!triggerElement || !menuElement) return;

    const margin = 8;
    const triggerRect = triggerElement.getBoundingClientRect();
    const menuRect = menuElement.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const availableBelow = viewportHeight - triggerRect.bottom - margin;
    const availableAbove = triggerRect.top - margin;
    // scrollHeight, not the rect: on first open the max-height is still 0, so
    // the rect reads 0 and every menu looked like it fit below.
    const menuHeight = menuElement.scrollHeight + 2;
    // Below if it fits there; otherwise above if it fits there; otherwise
    // whichever side is roomier, scrolling inside it.
    const opensBelow = menuHeight <= availableBelow || (menuHeight > availableAbove && availableBelow >= availableAbove);
    const maxHeight = Math.max(120, opensBelow ? availableBelow : availableAbove);
    const top = opensBelow
      ? triggerRect.bottom + 4
      : Math.max(margin, triggerRect.top - Math.min(menuHeight, maxHeight) - 4);
    const left = Math.min(
      Math.max(margin, triggerRect.right - menuRect.width),
      Math.max(margin, viewportWidth - menuRect.width - margin),
    );

    setDesktopPosition({ top, left, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    positionDesktopMenu();
    const onViewportChange = () => positionDesktopMenu();
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open, positionDesktopMenu]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  useEffect(() => {
    if (open) menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    else trigger.current?.focus();
  }, [open]);

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        ref={trigger}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('folders.menu', { name: nodeName })}
        className="tap-target flex items-center justify-center rounded-full text-muted hover:bg-surface-3 hover:text-fg"
      >
        {/* Same filled kebab as the grid toolbar's overflow trigger. */}
        <svg aria-hidden="true" viewBox="0 0 4 16" className="h-4 w-1 shrink-0 fill-current"><circle cx="2" cy="2.5" r="1.6" /><circle cx="2" cy="8" r="1.6" /><circle cx="2" cy="13.5" r="1.6" /></svg>
      </button>

      {open && (
        <>
          {/* Backdrop, phone only: it is what makes the sheet below readable. */}
          <div className="fixed inset-0 z-40 bg-[var(--overlay)] sm:hidden" aria-hidden="true" />
          <div
            ref={menu}
            role="menu"
            style={
              {
                '--folder-menu-top': `${desktopPosition.top}px`,
                '--folder-menu-left': `${desktopPosition.left}px`,
                '--folder-menu-max-height': `${desktopPosition.maxHeight}px`,
              } as CSSProperties
            }
            className={[
              'z-50 overflow-hidden border border-border bg-surface shadow-lg',
              'fixed inset-x-0 bottom-0 rounded-t-token-lg pb-[max(0.5rem,env(safe-area-inset-bottom))]',
              'sm:fixed sm:inset-x-auto sm:bottom-auto sm:left-[var(--folder-menu-left)] sm:top-[var(--folder-menu-top)] sm:mt-0 sm:max-h-[var(--folder-menu-max-height)] sm:w-56 sm:overflow-y-auto sm:rounded-token sm:pb-0',
            ].join(' ')}
          >
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                role="menuitem"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setOpen(false);
                  action.run();
                }}
                className={[
                  'tap-target flex w-full items-center gap-3 px-4 text-left text-sm sm:px-3',
                  action.danger ? 'text-danger hover:bg-danger-soft' : 'text-fg hover:bg-surface-3',
                ].join(' ')}
              >
                <Icon name={action.icon} className="h-4 w-4 shrink-0" />
                <span className="truncate">{t(action.labelKey)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}




